import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { usePermissionCheck, usePermissions } from '../../context/PermissionContext';

// 智能重定向组件
// 根据用户权限自动跳转到第一个有权限访问的页面
const SmartRedirect: React.FC = () => {
  const { loading } = usePermissions();
  const {
    canViewDashboard,
    canViewUsers,
    canViewAPIs,
    canViewModels,
    canViewRecharge,
    canViewRechargeOrders,
    canViewPreferences,
    canViewApiTest,
    canViewChat,
    canViewPermissions,
    canViewRoles,
    canViewUserRoles,
    canViewUserLogs,
    canViewAPILogs,
    canViewStats,
  } = usePermissionCheck();

  // 定义页面优先级列表（从高到低）
  // 优先显示常用的功能页面
  const pageRoutes = [
    { path: '/admin/dashboard', canAccess: canViewDashboard, label: '仪表盘' },
    { path: '/admin/apikeys', canAccess: canViewAPIs, label: 'API密钥管理' },
    { path: '/admin/api-test', canAccess: canViewApiTest, label: 'API测试' },
    { path: '/admin/chat', canAccess: canViewChat, label: 'AI聊天' },
    { path: '/admin/usage-info', canAccess: canViewPreferences, label: '使用信息' },
    { path: '/admin/users', canAccess: canViewUsers, label: '用户管理' },
    { path: '/admin/models', canAccess: canViewModels, label: '大模型管理' },
    { path: '/admin/recharge', canAccess: () => canViewRecharge && canViewRecharge(), label: '充值管理' },
    { path: '/admin/recharge-orders', canAccess: () => canViewRechargeOrders && canViewRechargeOrders(), label: '充值订单管理' },
    { path: '/admin/permissions', canAccess: canViewPermissions, label: '权限管理' },
    { path: '/admin/roles', canAccess: canViewRoles, label: '角色管理' },
    { path: '/admin/user-roles', canAccess: canViewUserRoles, label: '用户角色' },
    { path: '/admin/logs', canAccess: canViewUserLogs, label: '用户日志' },
    { path: '/admin/api-logs', canAccess: canViewAPILogs, label: 'API调用日志' },
    { path: '/admin/stats', canAccess: canViewStats, label: '统计信息' },
    { path: '/admin/settings', canAccess: () => true, label: '修改密码' }, // 所有用户都可以访问设置页面
  ];

  // 找到第一个有权限访问的页面
  const firstAccessiblePage = !loading ? pageRoutes.find(route => route.canAccess()) : null;

  // useEffect必须在条件语句之前调用
  useEffect(() => {
    if (!loading) {
      if (firstAccessiblePage) {
        console.log(`🎯 智能重定向到第一个可访问页面: ${firstAccessiblePage.label} (${firstAccessiblePage.path})`);
      } else {
        console.log('⚠️ 用户没有权限访问任何页面，重定向到设置页面');
      }
    }
  }, [loading, firstAccessiblePage]);

  // 如果权限还在加载中，显示加载状态
  if (loading) {
    return <div>正在加载权限信息...</div>;
  }

  // 如果找到有权限的页面，重定向到该页面
  if (firstAccessiblePage) {
    return <Navigate to={firstAccessiblePage.path} replace />;
  }

  // 如果用户没有任何页面权限，重定向到设置页面（所有用户都能访问）
  return <Navigate to="/admin/settings" replace />;
};

export default SmartRedirect; 